import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { ApiError, SettlingInService } from '@/api';
import type {
  SettlingInFilters,
  CreateSettlingInPlanRequest,
  UpdateSettlingInPlanRequest,
  BulkCreateScheduleEntriesRequest,
  UpdateScheduleEntryRequest,
  CreateTrackingEntryRequest,
  BulkCreateTrackingEntriesRequest,
  UpdateTrackingEntryRequest,
  CreateProgressEntryRequest,
  UpdateProgressEntryRequest,
  SignOffRequest,
} from '@/types/settlingIn';
import { toast } from 'sonner';
import { useLingui } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { getErrorMessage } from '@/utils/errorUtils';

// Query key factory for settling-in
export const settlingInKeys = {
  all: ['settlingIn'] as const,
  lists: () => [...settlingInKeys.all, 'list'] as const,
  // orgId is part of the key so a tenant switch can never serve the previous org's
  // rows on first paint (keepPreviousData below would otherwise show them).
  list: (filters: SettlingInFilters, orgId?: string | null) =>
    [...settlingInKeys.lists(), orgId ?? null, filters] as const,
  details: () => [...settlingInKeys.all, 'detail'] as const,
  detail: (id: string) => [...settlingInKeys.details(), id] as const,
  completionWarnings: (id: string) => [...settlingInKeys.all, 'completionWarnings', id] as const,
  // `asOf` is part of the key: the target period it resolves is what the caller
  // reads, so two different prospective sign-off dates must not share a cache slot.
  billingPreview: (id: string, asOf?: string) =>
    [...settlingInKeys.all, 'billingPreview', id, asOf ?? null] as const,
};

/**
 * Hook to fetch paginated settling-in plans with filters.
 *
 * `status` and `sort` (ST-3/S11) are optional: when omitted the endpoint keeps its
 * legacy unfiltered `start_date DESC` behaviour, which the dashboard, the cockpit
 * panels and the mobile client all depend on.
 */
export function useSettlingInPlans(
  filters: SettlingInFilters = {},
  options?: { enabled?: boolean },
) {
  const { isSignedIn } = useAuth();
  const { isAuthReady, orgId } = useAuthContext();

  return useQuery({
    // `filters` (incl. childId) and orgId are both in the key; no factory change needed.
    queryKey: settlingInKeys.list(filters, orgId),
    queryFn: () =>
      SettlingInService.getApiV1SettlingIn(
        filters.page ?? 1,
        filters.pageSize ?? 20,
        filters.groupId,
        filters.childcareId,
        filters.completed,
        filters.withContracts,
        filters.status,
        filters.sort,
        filters.childId,
      ),
    enabled: (options?.enabled ?? true) && isSignedIn && isAuthReady,
    staleTime: 30000,
    // A 401/403 (e.g. the settling-in module is not granted) is final: the prefill
    // cross-reads treat it as "nothing found" (#1685 D6), so never retry it.
    // `count < 1` keeps the app-wide single retry for everything else.
    retry: (count, error) =>
      !(error instanceof ApiError && [401, 403].includes(error.status)) && count < 1,
    // Keeps the previous page visible while the next one loads, instead of flashing
    // an empty table on every pagination or filter change.
    placeholderData: keepPreviousData,
  });
}

/**
 * Hook to fetch a single settling-in plan with details.
 */
export function useSettlingInPlan(id: string, options?: { enabled?: boolean }) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: settlingInKeys.detail(id),
    queryFn: () => SettlingInService.getApiV1SettlingIn1(id),
    enabled: (options?.enabled ?? true) && !!id && isSignedIn && isAuthReady,
    staleTime: 30000,
  });
}

/**
 * Hook to fetch completion warnings for a settling-in plan.
 */
export function useCompletionWarnings(id: string, enabled = false) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();
  return useQuery({
    queryKey: settlingInKeys.completionWarnings(id),
    queryFn: () => SettlingInService.getApiV1SettlingInCompletionWarnings(id),
    enabled: enabled && isSignedIn && isAuthReady && !!id,
    staleTime: 0,
    gcTime: 0,
  });
}

/**
 * Hook to fetch the READ-ONLY settling-in billing preview for a plan (ST-6/S18).
 *
 * `asOf` is the prospective sign-off date the operator is currently entering; it
 * resolves the target invoice period only and is never persisted.
 *
 * `staleTime: 0` / `gcTime: 0` mirror `useCompletionWarnings`: a dialog gating a
 * billing-triggering action must always show a current figure, at the cost of one
 * request per open. The figure is advisory — it takes no lock and is not a
 * reservation.
 */
export function useSettlingInBillingPreview(id: string, enabled = false, asOf?: string) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();
  return useQuery({
    queryKey: settlingInKeys.billingPreview(id, asOf),
    queryFn: () => SettlingInService.getApiV1SettlingInBillingPreview(id, asOf),
    enabled: enabled && isSignedIn && isAuthReady && !!id,
    staleTime: 0,
    gcTime: 0,
  });
}

/**
 * Hook to create a new settling-in plan.
 */
export function useCreateSettlingIn() {
  const queryClient = useQueryClient();
  const { t } = useLingui();

  return useMutation({
    mutationFn: (data: CreateSettlingInPlanRequest) =>
      SettlingInService.postApiV1SettlingIn(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settlingInKeys.all });
      toast.success(t({ id: 'settlingIn.success.created', message: 'Settling-in plan created successfully' }));
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, t({ id: 'settlingIn.error.createFailed', message: 'Failed to create settling-in plan' })));
    },
  });
}

/**
 * Hook to update a settling-in plan.
 */
export function useUpdateSettlingIn() {
  const queryClient = useQueryClient();
  const { t } = useLingui();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSettlingInPlanRequest }) =>
      SettlingInService.putApiV1SettlingIn(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: settlingInKeys.all });
      queryClient.invalidateQueries({ queryKey: settlingInKeys.detail(id) });
      toast.success(t({ id: 'settlingIn.success.updated', message: 'Settling-in plan updated successfully' }));
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, t({ id: 'settlingIn.error.updateFailed', message: 'Failed to update settling-in plan' })));
    },
  });
}

/**
 * Hook to delete a settling-in plan.
 */
export function useDeleteSettlingIn() {
  const queryClient = useQueryClient();
  const { t } = useLingui();

  return useMutation({
    mutationFn: (id: string) => SettlingInService.deleteApiV1SettlingIn(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settlingInKeys.all });
      toast.success(t({ id: 'settlingIn.success.deleted', message: 'Settling-in plan deleted successfully' }));
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, t({ id: 'settlingIn.error.deleteFailed', message: 'Failed to delete settling-in plan' })));
    },
  });
}

/**
 * Hook to mark a settling-in plan as completed.
 */
export function useCompleteSettlingIn() {
  const queryClient = useQueryClient();
  const { t } = useLingui();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { end_date: string } }) =>
      SettlingInService.postApiV1SettlingInComplete(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: settlingInKeys.all });
      queryClient.invalidateQueries({ queryKey: settlingInKeys.detail(id) });
      toast.success(t({ id: 'settlingIn.success.completed', message: 'Settling-in plan completed' }));
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, t({ id: 'settlingIn.error.completeFailed', message: 'Failed to complete settling-in plan' })));
    },
  });
}

/**
 * Hook to sign off a settling-in plan.
 */
export function useSignOffSettlingIn() {
  const queryClient = useQueryClient();
  const { t } = useLingui();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SignOffRequest }) =>
      SettlingInService.postApiV1SettlingInSignOff(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: settlingInKeys.all });
      queryClient.invalidateQueries({ queryKey: settlingInKeys.detail(id) });
      toast.success(t({ id: 'settlingIn.success.signedOff', message: 'Settling-in plan signed off' }));
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, t({ id: 'settlingIn.error.signOffFailed', message: 'Failed to sign off settling-in plan' })));
    },
  });
}

/**
 * Hook to update the end date of a completed settling-in plan.
 */
export function useUpdateCompleteSettlingIn() {
  const queryClient = useQueryClient();
  const { t } = useLingui();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { end_date: string } }) =>
      SettlingInService.putApiV1SettlingInComplete(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: settlingInKeys.all });
      queryClient.invalidateQueries({ queryKey: settlingInKeys.detail(id) });
      toast.success(t({ id: 'settlingIn.success.completionUpdated', message: 'Completion updated' }));
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, t({ id: 'settlingIn.error.completionUpdateFailed', message: 'Failed to update completion' })));
    },
  });
}

/**
 * Hook to undo completion of a settling-in plan.
 */
export function useUndoCompleteSettlingIn() {
  const queryClient = useQueryClient();
  const { t } = useLingui();

  return useMutation({
    mutationFn: (id: string) =>
      SettlingInService.deleteApiV1SettlingInComplete(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: settlingInKeys.all });
      queryClient.invalidateQueries({ queryKey: settlingInKeys.detail(id) });
      toast.success(t({ id: 'settlingIn.success.completionUndone', message: 'Completion undone' }));
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, t({ id: 'settlingIn.error.completionUndoFailed', message: 'Failed to undo completion' })));
    },
  });
}

/**
 * Hook to undo sign-off of a settling-in plan.
 */
export function useUndoSignOffSettlingIn() {
  const queryClient = useQueryClient();
  const { t } = useLingui();

  return useMutation({
    mutationFn: (id: string) =>
      SettlingInService.deleteApiV1SettlingInSignOff(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: settlingInKeys.all });
      queryClient.invalidateQueries({ queryKey: settlingInKeys.detail(id) });
      toast.success(t({ id: 'settlingIn.success.signOffUndone', message: 'Sign-off undone' }));
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, t({ id: 'settlingIn.error.signOffUndoFailed', message: 'Failed to undo sign-off' })));
    },
  });
}

// --- Schedule Entry Hooks ---

/**
 * Hook to create schedule entries (bulk). `silent` suppresses the success toast for a caller
 * that runs it as part of a larger action and reports once.
 */
export function useCreateScheduleEntries(options?: { silent?: boolean }) {
  const silent = options?.silent ?? false;
  const queryClient = useQueryClient();
  const { t } = useLingui();

  return useMutation({
    mutationFn: ({ planId, data }: { planId: string; data: BulkCreateScheduleEntriesRequest }) =>
      SettlingInService.postApiV1SettlingInSchedule(planId, data),
    onSuccess: (_, { planId }) => {
      queryClient.invalidateQueries({ queryKey: settlingInKeys.detail(planId) });
      if (!silent) toast.success(t({ id: 'settlingIn.schedule.success.created', message: 'Schedule entry created' }));
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, t({ id: 'settlingIn.schedule.error.createFailed', message: 'Failed to create schedule entry' })));
    },
  });
}

/**
 * Hook to update a schedule entry.
 */
export function useUpdateScheduleEntry() {
  const queryClient = useQueryClient();
  const { t } = useLingui();

  return useMutation({
    mutationFn: ({ planId, entryId, data }: { planId: string; entryId: string; data: UpdateScheduleEntryRequest }) =>
      SettlingInService.putApiV1SettlingInSchedule(planId, entryId, data),
    onSuccess: (_, { planId }) => {
      queryClient.invalidateQueries({ queryKey: settlingInKeys.detail(planId) });
      toast.success(t({ id: 'settlingIn.schedule.success.updated', message: 'Schedule entry updated' }));
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, t({ id: 'settlingIn.schedule.error.updateFailed', message: 'Failed to update schedule entry' })));
    },
  });
}

/**
 * Hook to delete a schedule entry.
 */
export function useDeleteScheduleEntry() {
  const queryClient = useQueryClient();
  const { t } = useLingui();

  return useMutation({
    mutationFn: ({ planId, entryId }: { planId: string; entryId: string }) =>
      SettlingInService.deleteApiV1SettlingInSchedule(planId, entryId),
    onSuccess: (_, { planId }) => {
      queryClient.invalidateQueries({ queryKey: settlingInKeys.detail(planId) });
      toast.success(t({ id: 'settlingIn.schedule.success.deleted', message: 'Schedule entry deleted' }));
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, t({ id: 'settlingIn.schedule.error.deleteFailed', message: 'Failed to delete schedule entry' })));
    },
  });
}

// --- Tracking Entry Hooks ---

/**
 * Hook to create a tracking entry.
 */
export function useCreateTrackingEntry() {
  const queryClient = useQueryClient();
  const { t } = useLingui();

  return useMutation({
    mutationFn: ({ planId, data }: { planId: string; data: CreateTrackingEntryRequest }) =>
      SettlingInService.postApiV1SettlingInTracking(planId, data),
    onSuccess: (_, { planId }) => {
      queryClient.invalidateQueries({ queryKey: settlingInKeys.detail(planId) });
      toast.success(t({ id: 'settlingIn.tracking.success.created', message: 'Tracking entry created' }));
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, t({ id: 'settlingIn.tracking.error.createFailed', message: 'Failed to create tracking entry' })));
    },
  });
}

/**
 * Hook to bulk-create tracking entries in a single backend call ("Copy planned → actual",
 * mark-absent-for-range). One atomic POST seeds N tracking rows instead of N requests.
 */
export function useBulkCreateTrackingEntries() {
  const queryClient = useQueryClient();
  const { t, i18n } = useLingui();

  return useMutation({
    mutationFn: ({ planId, data }: { planId: string; data: BulkCreateTrackingEntriesRequest }) =>
      SettlingInService.postApiV1SettlingInTrackingBulk(planId, data),
    onSuccess: (created, { planId }) => {
      queryClient.invalidateQueries({ queryKey: settlingInKeys.detail(planId) });
      // The hook backs both "Copy planned → actual" and "Mark absent for a range", so the
      // message has to describe the outcome both share rather than naming one of them.
      toast.success(
        i18n._(
          msg({
            id: 'settlingIn.tracking.success.bulkCreated',
            message: '{count, plural, one {# day recorded} other {# days recorded}}',
          }),
          { count: created?.length ?? 0 }
        )
      );
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, t({ id: 'settlingIn.tracking.error.bulkCreateFailed', message: 'Failed to record days' })));
    },
  });
}

/**
 * Hook to update a tracking entry.
 */
export function useUpdateTrackingEntry() {
  const queryClient = useQueryClient();
  const { t } = useLingui();

  return useMutation({
    mutationFn: ({ planId, entryId, data }: { planId: string; entryId: string; data: UpdateTrackingEntryRequest }) =>
      SettlingInService.putApiV1SettlingInTracking(planId, entryId, data),
    onSuccess: (_, { planId }) => {
      queryClient.invalidateQueries({ queryKey: settlingInKeys.detail(planId) });
      toast.success(t({ id: 'settlingIn.tracking.success.updated', message: 'Tracking entry updated' }));
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, t({ id: 'settlingIn.tracking.error.updateFailed', message: 'Failed to update tracking entry' })));
    },
  });
}

/**
 * Hook to delete a tracking entry. `silent` suppresses the success toast for a caller
 * that runs it as part of a larger action and reports once.
 */
export function useDeleteTrackingEntry(options?: { silent?: boolean }) {
  const silent = options?.silent ?? false;
  const queryClient = useQueryClient();
  const { t } = useLingui();

  return useMutation({
    mutationFn: ({ planId, entryId }: { planId: string; entryId: string }) =>
      SettlingInService.deleteApiV1SettlingInTracking(planId, entryId),
    onSuccess: (_, { planId }) => {
      queryClient.invalidateQueries({ queryKey: settlingInKeys.detail(planId) });
      if (!silent) toast.success(t({ id: 'settlingIn.tracking.success.deleted', message: 'Tracking entry deleted' }));
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, t({ id: 'settlingIn.tracking.error.deleteFailed', message: 'Failed to delete tracking entry' })));
    },
  });
}

// --- Progress Entry Hooks ---

/**
 * Hook to create a progress entry.
 */
export function useCreateProgressEntry() {
  const queryClient = useQueryClient();
  const { t } = useLingui();

  return useMutation({
    mutationFn: ({ planId, data }: { planId: string; data: CreateProgressEntryRequest }) =>
      SettlingInService.postApiV1SettlingInProgress(planId, data),
    onSuccess: (_, { planId }) => {
      queryClient.invalidateQueries({ queryKey: settlingInKeys.detail(planId) });
      toast.success(t({ id: 'settlingIn.progress.success.created', message: 'Progress entry created' }));
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, t({ id: 'settlingIn.progress.error.createFailed', message: 'Failed to create progress entry' })));
    },
  });
}

/**
 * Hook to update a progress entry.
 */
export function useUpdateProgressEntry() {
  const queryClient = useQueryClient();
  const { t } = useLingui();

  return useMutation({
    mutationFn: ({ planId, entryId, data }: { planId: string; entryId: string; data: UpdateProgressEntryRequest }) =>
      SettlingInService.putApiV1SettlingInProgress(planId, entryId, data),
    onSuccess: (_, { planId }) => {
      queryClient.invalidateQueries({ queryKey: settlingInKeys.detail(planId) });
      toast.success(t({ id: 'settlingIn.progress.success.updated', message: 'Progress entry updated' }));
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, t({ id: 'settlingIn.progress.error.updateFailed', message: 'Failed to update progress entry' })));
    },
  });
}

/**
 * Hook to delete a progress entry.
 */
export function useDeleteProgressEntry() {
  const queryClient = useQueryClient();
  const { t } = useLingui();

  return useMutation({
    mutationFn: ({ planId, entryId }: { planId: string; entryId: string }) =>
      SettlingInService.deleteApiV1SettlingInProgress(planId, entryId),
    onSuccess: (_, { planId }) => {
      queryClient.invalidateQueries({ queryKey: settlingInKeys.detail(planId) });
      toast.success(t({ id: 'settlingIn.progress.success.deleted', message: 'Progress entry deleted' }));
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, t({ id: 'settlingIn.progress.error.deleteFailed', message: 'Failed to delete progress entry' })));
    },
  });
}
